import React, { useState } from "react";
import { useAuth } from "../../App";
import { useNavigate, useLocation } from 'react-router-dom';
import "./login.css";
import logo from '../../upload/logo.jpg';
import AccessDeniedModal from '../common/AccessDeniedModal';

export default function LoginForm() {
  const { login, apiUrl } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({ username: '', password: '' });
  const [touched, setTouched] = useState({ username: false, password: false });
  const [submitAttempt, setSubmitAttempt] = useState(false);
  const [loginError, setLoginError] = useState('');

  const [showAccessDenied, setShowAccessDenied] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState('You do not have permission to access this page.');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempt(true);

  // reset any previous login-level error
  setLoginError('');

  // client-side validation
    const newErrors = { username: '', password: '' };
    if (!username || !String(username).trim()) newErrors.username = 'Username is required';
    if (!password || !String(password).trim()) newErrors.password = 'Password is required';
    setErrors(newErrors);
    if (newErrors.username || newErrors.password) return;

    let u = null;
    try {
      // First, call the login endpoint and fetch the profile to get the role
      // so we can enforce device-role policy before completing the app login.
      if (!apiUrl) {
        // fallback to normal login if API not configured
        u = await login(username, password);
        if (!u) return;
      } else {
        const res = await fetch(`${apiUrl}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, password }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || 'Login failed');
        }
        const data = await res.json();
        const token = data.token;

        // fetch profile to check role
        const meRes = await fetch(`${apiUrl}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (!meRes.ok) {
          const d = await meRes.json().catch(() => ({}));
          throw new Error(d.message || 'Failed to fetch profile');
        }
        const md = await meRes.json();
        const uRole = (md && (md.user || md) && (md.user || md).role) ? String((md.user || md).role).toLowerCase() : '';

        const isMobile = (typeof window !== 'undefined') ? (window.innerWidth <= 900) : false;
        const isDesktop = !isMobile;

        const adminLike = ['admin', 'teacher', 'mod'];
        const blockedForMobile = adminLike.includes(uRole) && isMobile;
        const blockedForDesktop = (uRole === 'student') && isDesktop;

        if (blockedForMobile || blockedForDesktop) {
          const message = blockedForMobile
            ? 'Admin/Teacher accounts should log in using a desktop/laptop for the best experience. Please use a desktop device.'
            : 'Student accounts should log in using a mobile device for the best experience. Please use a mobile device.';
          if (window && window.showToast) {
            const position = blockedForMobile ? 'center' : 'top-right';
            window.showToast(message, 'info', 6000, position);
          } else try { alert(message); } catch (e) { }
          return; // do not proceed to app login
        }

        // allowed — proceed to app login which will persist token and set user
        u = await login(username, password);
        if (!u) return;
      }
    } catch (err) {
      console.error(err);
      // map common backend messages like "Invalid credentials" to Vietnamese message
      const raw = (err && err.message) ? String(err.message) : '';
      const low = raw.toLowerCase();
      let mapped = raw || 'Login failed';
      if (low.includes('invalid') || low.includes('credentials') || low.includes('credential') || low.includes('wrong password') || (low.includes('username') && low.includes('password'))) {
        mapped = 'Username or password is incorrect.';
      }
      setLoginError(mapped);
      try { alert(mapped); } catch (e) { }
      return;
    }

    const currentPath = location?.pathname || '';

    // Nếu người dùng đang cố truy cập trang /admin (ví dụ trước khi login)
    if (currentPath.startsWith('/admin')) {
      if (u.role === 'Admin') {
        navigate('/admin', { replace: true });
        return;
      }
      if (u.role === 'Mod') {
        navigate('/admin/accounts', { replace: true });
        return;
      }

      // Không đủ quyền → show modal
      setAccessDeniedMessage('You do not have permission to access this page.');
      setShowAccessDenied(true);
      return;
    }

    // Trường hợp login bình thường: chuyển theo role
    if (u.role === 'Admin') {
      navigate('/admin', { replace: true });
      return;
    } else if (u.role === 'Mod') {
      // Mods should default to app root on login; accessing /admin later will route to /admin/accounts
      navigate('/', { replace: true });
      return;
    } else {
      navigate('/', { replace: true });
      return;
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="brand">
          <img src={logo} alt="logo" height={100} width={250} />
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {loginError ? <div className="error">{loginError}</div> : null}
          <div className="field">
            <input
              className="input"
              placeholder="Username"
              value={username}
              onChange={(e) => { setUsername(e.target.value); if (errors.username) setErrors(prev => ({ ...prev, username: '' })); if (loginError) setLoginError(''); }}
              onBlur={() => setTouched(prev => ({ ...prev, username: true }))}
            />
            {(errors.username && (touched.username || submitAttempt)) ? <div className="error">{errors.username}</div> : null}
          </div>
          <div className="field">
            <input
              className="input"
              placeholder="Password"
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); if (errors.password) setErrors(prev => ({ ...prev, password: '' })); if (loginError) setLoginError(''); }}
              onBlur={() => setTouched(prev => ({ ...prev, password: true }))}
            />
            {(errors.password && (touched.password || submitAttempt)) ? <div className="error">{errors.password}</div> : null}
          </div>
          <button className="btn btn-primary" type="submit">Login</button>
        </form>

        <p className="demo">Demo: admin/123456</p>
      </div>
      <AccessDeniedModal show={showAccessDenied} message={accessDeniedMessage} />
    </div>
  );
}
